import { Directive, ElementRef, Renderer2, HostListener, OnDestroy, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import { CoreLanguageService } from '../../services/language.service';

@Directive({
  selector: `
    button, [a11y_Button], [a11y_Slider], [a11y_NoAria], [a11y_Input], [a11y_SpinButton], [a11y_Select], [a11y_Checkbox], [a11y_Link]
  `
})
export class ButtonPropertiesDirective implements AfterViewInit, OnDestroy {
  private langSubscriber: Subscription = null;

  private attributeConfig: { [key: string]: { role: string; tabindex: string; ariaKey?: string; ariaLive?: string } } = {
    a11y_Slider: { role: 'region', tabindex: '-1', ariaKey: 'phA11Y_slider', ariaLive: 'polite' },
    a11y_NoAria: { role: 'none', tabindex: '-1', ariaKey: 'phA11Y_no_aria' },
    a11y_Button: { role: 'button', tabindex: '0', ariaKey: 'phA11Y_button' },
    a11y_SpinButton: { role: 'spinbutton', tabindex: '0', ariaKey: 'phA11Y_spin_button' },
    a11y_Input: { role: 'textbox', tabindex: '0', ariaKey: 'phA11Y_input' },
    a11y_Select: { role: 'listbox', tabindex: '0', ariaKey: 'phA11Y_select' },
    a11y_Checkbox: { role: 'checkbox', tabindex: '0', ariaKey: 'phA11Y_checkbox' },
    a11y_Link: { role: 'link', tabindex: '0', ariaKey: 'phA11Y_link' }
  };

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private internationalisationService: InternationalisationService,
    private coreLanguageService: CoreLanguageService
  ) { }

  ngAfterViewInit(): void {
    this.updateA11yAttributes();

    this.langSubscriber = this.internationalisationService.localeChanged.subscribe(() => {
      this.updateA11yAttributes();
    });
  }

  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    const element = this.el.nativeElement;

    // a11y_Select: simulate space on select
    if (element.hasAttribute('a11y_Select')) {
      event.preventDefault();
      const selectEl = element.querySelector('select');
      if (selectEl) {
        const spaceEvent = new KeyboardEvent('keydown', { key: ' ', bubbles: true });
        selectEl.dispatchEvent(spaceEvent);
      }
      return;
    }

    // role=tab: just click
    if (element.getAttribute('role') === 'tab') {
      event.preventDefault();
      if (typeof element.click === 'function') {
        element.click();
      }
      return;
    }

    // a11y_Checkbox: click the checkbox input
    if (element.hasAttribute('a11y_Checkbox')) {
      event.preventDefault();
      const checkbox = element.querySelector('mat-checkbox input[type="checkbox"]');
      if (checkbox) {
        (checkbox as HTMLInputElement).click();
      }
      return;
    }

    // a11y_Button or a11y_Link: fire click
    if (
      element.hasAttribute('a11y_Button') ||
      element.hasAttribute('a11y_Link') ||
      element.getAttribute('role') === 'button' ||
      element.getAttribute('role') === 'link'
    ) {
      event.preventDefault();
      if (typeof element.click === 'function') {
        element.click();
      } else {
        const mouseEvent = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
        element.dispatchEvent(mouseEvent);
      }
      return;
    }
  }

  private updateA11yAttributes(): void {
    var element = this.el.nativeElement;

    for (var key in this.attributeConfig) {
      if (this.attributeConfig.hasOwnProperty(key) && element.hasAttribute(key)) {
        var config = this.attributeConfig[key];

        this.renderer.setAttribute(element, 'role', config.role);
        this.renderer.setAttribute(element, 'tabindex', config.tabindex);

        if (config.ariaKey) {
          var translated = this.coreLanguageService.value(config.ariaKey);

          if (key === 'a11y_NoAria') {
            this.renderer.setAttribute(element, 'aria-hidden', translated);
          } else if (key === 'a11y_Button' || key === 'a11y_Input' || key === 'a11y_SpinButton') {
            this.setAriaLabelIfMissing(element, translated);

            if (key === 'a11y_SpinButton') {
              this.setDefaultSpinAttributes(element);
            }
          } else {
            this.renderer.setAttribute(element, 'aria-roledescription', translated);
          }
        }

        // Setze aria-live, falls definiert
        if (config.ariaLive) {
          this.renderer.setAttribute(element, 'aria-live', config.ariaLive);
        }

        break;
      }
    }
  }

  private setAriaLabelIfMissing(element: HTMLElement, label: string): void {
    if (!element.hasAttribute('aria-label') && !element.hasAttribute('aria-labelledby')) {
      this.renderer.setAttribute(element, 'aria-label', label || 'Label');
    }
  }

  private setDefaultSpinAttributes(element: HTMLElement): void {
    if (!element.hasAttribute('aria-valuenow')) {
      this.renderer.setAttribute(element, 'aria-valuenow', '0');
    }
    if (!element.hasAttribute('aria-valuemin')) {
      this.renderer.setAttribute(element, 'aria-valuemin', '0');
    }
    if (!element.hasAttribute('aria-valuemax')) {
      this.renderer.setAttribute(element, 'aria-valuemax', '99');
    }
  }

  ngOnDestroy(): void {
    if (this.langSubscriber) {
      this.langSubscriber.unsubscribe();
    }
  }
}